import * as React from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Check from "@mui/icons-material/Check";
import { useTranslation } from "react-i18next";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import { Box, Typography, Grid } from "@mui/material";
import { makeStyles } from "@mui/styles";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import DomainVerificationIcon from "@mui/icons-material/DomainVerification";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import AttachEmailIcon from "@mui/icons-material/AttachEmail";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ArrowCircleRightOutlinedIcon from "@mui/icons-material/ArrowCircleRightOutlined";
import ArrowCircleDownOutlinedIcon from "@mui/icons-material/ArrowCircleDownOutlined";

const QontoStepIconRoot = styled("div")(({ theme, ownerState }) => ({
  color: theme.palette.mode === "dark" ? theme.palette.grey[700] : "#eaeaf0",
  display: "flex",
  height: 22,
  alignItems: "center",
  ...(ownerState.active && {
    color: "#784af4",
  }),
  "& .QontoStepIcon-completedIcon": {
    color: "#784af4",
    zIndex: 1,
    fontSize: 18,
  },
  "& .QontoStepIcon-circle": {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "currentColor",
  },
}));

function QontoStepIcon(props) {
  const { t, i18n } = useTranslation();
  const { active, completed, className } = props;

  return (
    <QontoStepIconRoot ownerState={{ active }} className={className}>
      {completed ? (
        <Check className="QontoStepIcon-completedIcon" />
      ) : (
        <div className="QontoStepIcon-circle" />
      )}
    </QontoStepIconRoot>
  );
}

QontoStepIcon.propTypes = {
  /**
   * Whether this step is active.
   * @default false
   */
  active: PropTypes.bool,
  className: PropTypes.string,
  /**
   * Mark the step as completed. Is passed to child components.
   * @default false
   */
  completed: PropTypes.bool,
};

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage:
        "linear-gradient( 95deg,rgb(242,113,33) 0%,rgb(233,64,87) 50%,rgb(138,35,135) 100%)",
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage:
        "linear-gradient( 95deg,rgb(242,113,33) 0%,rgb(233,64,87) 50%,rgb(138,35,135) 100%)",
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor:
      theme.palette.mode === "dark" ? theme.palette.grey[800] : "#eaeaf0",
    borderRadius: 1,
  },
}));

const ColorlibStepIconRoot = styled("div")(({ theme, ownerState }) => ({
  backgroundColor:
    theme.palette.mode === "dark" ? theme.palette.grey[700] : "#ccc",
  zIndex: 1,
  color: "#fff",
  width: 50,
  height: 50,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
  ...(ownerState.active && {
    backgroundImage:
      "linear-gradient( 136deg, rgb(242,113,33) 0%, rgb(233,64,87) 50%, rgb(138,35,135) 100%)",
    boxShadow: "0 4px 10px 0 rgba(0,0,0,.25)",
  }),
  ...(ownerState.completed && {
    backgroundImage:
      "linear-gradient( 136deg, rgb(242,113,33) 0%, rgb(233,64,87) 50%, rgb(138,35,135) 100%)",
  }),
}));

function ColorlibStepIcon(props) {
  const { active, completed, className } = props;

  const icons = {
    1: <ShoppingBagIcon />,
    2: <DomainVerificationIcon />,
    3: <Inventory2Icon />,
    4: <AttachEmailIcon />,
    5: <LocalShippingIcon />,
  };

  return (
    <ColorlibStepIconRoot
      ownerState={{ completed, active }}
      className={className}
    >
      {icons[String(props.icon)]}
    </ColorlibStepIconRoot>
  );
}

ColorlibStepIcon.propTypes = {
  /**
   * Whether this step is active.
   * @default false
   */
  active: PropTypes.bool,
  className: PropTypes.string,
  /**
   * Mark the step as completed. Is passed to child components.
   * @default false
   */
  completed: PropTypes.bool,
  /**
   * The label displayed in the step icon.
   */
  icon: PropTypes.node,
};

const useStyles = makeStyles({
  box: {},
  box2: {
    border: "1px solid lightgray",
    borderRadius: "30%",
    width: "125px",
    height: "80px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: "1.4em",
    marginTop: "20px",
  },
});

const Sourcing = () => {
  const { t, i18n } = useTranslation();
  const classes = useStyles();

  const steps = [
    t("sourcing1"),
    t("sourcing2"),
    t("sourcing3"),
    t("sourcing4"),
    t("sourcing5"),
  ];

  const CustomizedSteppers = () => {
    return (
      <Stack sx={{ width: "100%" }} spacing={4}>
        <Stepper
          alternativeLabel
          activeStep={5}
          connector={<ColorlibConnector />}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel StepIconComponent={ColorlibStepIcon}>
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Stack>
    );
  };
  return (
    <Box sx={{ textAlign: "center", pt: 10, width: 1, pb: 20 }}>
      <Typography
        variant="h4"
        dangerouslySetInnerHTML={{ __html: t("company_sourcing") }}
      ></Typography>
      <Typography
        sx={{
          width: 2 / 3,
          marginLeft: "auto",
          marginRight: "auto",
          pt: 3,
          lineHeight: "2.2em",
        }}
      >
        <Typography
          dangerouslySetInnerHTML={{ __html: t("company_sourcing2") }}
        />
      </Typography>
      <Box
        sx={{
          width: 1,
          display: "flex",
          justifyContent: "center",
          pt: 6,
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box sx={{ width: 2 / 3 }}>
          <CustomizedSteppers />
        </Box>
        <Box sx={{ pt: 4 }}>
          {i18n.language === "en" ? (
            <img src="/img/sourcing_en.jpeg" style={{ width: "70%" }} />
          ) : (
            <img src="/img/sourcing_ko.jpeg" style={{ width: "70%" }} />
          )}
        </Box>
        {/* <Grid container sx={{ width: 3 / 4, pt: 3 }}>
          <Grid item md={9} sx={{ display: "flex" }}>
            <Grid container>
              <Grid item sm={3} className={classes.box}>
                <Box className={classes.box2}>{t("sourcing6")}</Box>
              </Grid>
              <Grid item sm={3} className={classes.box}>
                <Box className={classes.box2}>{t("sourcing7")}</Box>
              </Grid>
              <Grid item sm={3} className={classes.box}>
                <Box className={classes.box2}>{t("sourcing8")}</Box>
              </Grid>
              <Grid item sm={3} className={classes.box}>
                <Box className={classes.box2}>{t("sourcing9")}</Box>
              </Grid>
              <Grid item sm={3} className={classes.box}>
                <Box className={classes.box2}>{t("sourcing10")}</Box>
              </Grid>
              <Grid item sm={3} className={classes.box}>
                <Box className={classes.box2}>{t("sourcing11")}</Box>
              </Grid>
              <Grid item sm={3} className={classes.box}>
                <Box className={classes.box2}>{t("sourcing12")}</Box>
              </Grid>
              <Grid item sm={3} className={classes.box}>
                <Box className={classes.box2}>{t("sourcing13")}</Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            item
            md={3}
            sm={12}
            sx={{ display: { md: "flex", sm: "none" } }}
          >
            <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
              <ArrowCircleRightOutlinedIcon
                sx={{ fontSize: "42px", marginTop: "95px" }}
              />
            </Box>
            <Box
              sx={{
                paddingTop: { md: "100px", sm: "90px" },
                paddingLeft: "50px",
                flex: 1,
                // border: "1px solid black",
              }}
            >
              <Typography color="secondary" sx={{ fontSize: "22px" }}>
                {t("sourcing14")}
              </Typography>
            </Box>
          </Grid>
          <Grid
            item
            md={3}
            sm={12}
            sx={{
              display: { sm: "flex", md: "none" },
              flexDirection: "column",
              // border: "1px solid black",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", pt: 2 }}>
              <ArrowCircleDownOutlinedIcon sx={{ fontSize: "42px" }} />
            </Box>
            <Box sx={{ pt: 2 }}>
              <Typography color="secondary" sx={{ fontSize: "22px" }}>
                {t("sourcing14")}
              </Typography>
            </Box>
          </Grid>
        </Grid> */}
      </Box>
    </Box>
  );
};

export default Sourcing;
